/**
 * Per-tenant drip-email log panel (OCP-85).
 *
 * Slots into the AdminTenantDetailPage. Shows current drip_state, the
 * full send log, and operator actions (pause / resume / cancel).
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../lib/api';

interface LogItem {
  id: string;
  stage: string;
  locale: string;
  variant: string;
  template_version: number;
  recipient_email: string;
  usesend_message_id: string | null;
  status: string;
  error_message: string | null;
  sent_at: string;
}

interface LogResponse {
  tenant_id: string;
  drip_state: string;
  drip_paused_at: string | null;
  locale: string;
  items: LogItem[];
}

const STATE_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
  day_1_sent: { label: 'Day 1 sent', cls: 'bg-sky-100 text-sky-800' },
  day_3_sent: { label: 'Day 3 sent', cls: 'bg-sky-100 text-sky-800' },
  day_7_sent: { label: 'Day 7 sent', cls: 'bg-sky-100 text-sky-800' },
  day_14_sent: { label: 'Day 14 sent', cls: 'bg-emerald-100 text-emerald-800 dark:text-emerald-300' },
  complete: { label: 'Complete', cls: 'bg-emerald-100 text-emerald-800 dark:text-emerald-300' },
  paused: { label: 'Paused', cls: 'bg-amber-100 text-amber-900 dark:text-amber-200' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400' },
};

export function DripLogPanel({ tenantId }: { tenantId: string }) {
  const [data, setData] = useState<LogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api<LogResponse>(`/v1/admin/drip/tenants/${tenantId}/log`);
      // a body without `items` (proxy error page, partial response) must not take the tenant page down
      setData({ ...r, items: Array.isArray(r?.items) ? r.items : [] });
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Could not load drip log.');
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (verb: 'pause' | 'resume' | 'cancel') => {
    setBusy(true);
    try {
      await api(`/v1/admin/drip/tenants/${tenantId}/${verb}`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Action HTTP ${err.status}` : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
        {error}
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-gray-500">Loading drip log...</p>;
  }
  const badge = STATE_BADGE[data.drip_state] ?? {
    label: data.drip_state,
    cls: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  const canPause = !['paused', 'cancelled', 'complete'].includes(data.drip_state);
  const canResume = data.drip_state === 'paused';
  const canCancel = data.drip_state !== 'cancelled';
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Onboarding drip</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Locale: {data.locale}
            {data.drip_paused_at && ` - paused at ${formatDate(data.drip_paused_at)}`}
          </p>
        </div>
        <span
          data-testid="drip-state-badge"
          className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
        >
          {badge.label}
        </span>
      </div>

      <div className="mb-3 flex gap-2">
        <button
          type="button"
          disabled={!canPause || busy}
          data-testid="drip-pause"
          onClick={() => act('pause')}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
        >
          Pause
        </button>
        <button
          type="button"
          disabled={!canResume || busy}
          data-testid="drip-resume"
          onClick={() => act('resume')}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
        >
          Resume
        </button>
        <button
          type="button"
          disabled={!canCancel || busy}
          data-testid="drip-cancel"
          onClick={() => act('cancel')}
          className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs text-red-800 dark:text-red-300 hover:bg-red-100 disabled:opacity-50"
        >
          Cancel (terminal)
        </button>
      </div>

      {data.items.length === 0 ? (
        <p className="text-xs text-gray-500">No drip emails sent yet.</p>
      ) : (
        <table className="w-full border-collapse text-xs">
          <thead className="text-left text-gray-500">
            <tr>
              <th className="py-1">Stage</th>
              <th className="py-1">Status</th>
              <th className="py-1">Locale</th>
              <th className="py-1">Variant</th>
              <th className="py-1">Version</th>
              <th className="py-1">Sent at</th>
              <th className="py-1">Message id</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((row) => (
              <tr key={row.id} data-testid={`drip-log-${row.stage}-${row.id}`} className="border-t border-gray-100">
                <td className="py-1">{row.stage}</td>
                <td className="py-1">
                  <span
                    className={
                      row.status === 'sent'
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-red-700 dark:text-red-300'
                    }
                  >
                    {row.status}
                  </span>
                  {row.error_message && (
                    <div className="text-[10px] text-red-700 dark:text-red-300">{row.error_message}</div>
                  )}
                </td>
                <td className="py-1">{row.locale}</td>
                <td className="py-1">{row.variant}</td>
                <td className="py-1">v{row.template_version}</td>
                <td className="py-1">{formatDate(row.sent_at)}</td>
                <td className="py-1 font-mono text-[10px] text-gray-500">
                  {row.usesend_message_id ?? '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
